import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Autocomplete, Box, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import {
	ALL_PRODUCT_SIZE_ID,
	ALL_PRODUCT_SIZE_OPTION,
	CREW_OPTIONS,
	type ProductSizeOption,
	SHIFT_OPTIONS,
} from "../models/StraightenerKpis";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	productSizeId: number | null;
	onProductSizeChange: (id: number | null) => void;
	productSizes: ProductSizeOption[];
	productTypeId: number | null;
	productTypes: { Id: number; Name: string }[];
	onProductTypeChange: (id: number | null) => void;
	productionShift: string | null;
	onProductionShiftChange: (shift: string | null) => void;
	crewId: string | null;
	onCrewChange: (crew: string | null) => void;
	onReset: () => void;
	onExportExcel: () => void;
	canReset: boolean;
}

export const StraightenerKpisToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	productSizeId,
	onProductSizeChange,
	productSizes,
	productTypeId,
	productTypes,
	onProductTypeChange,
	productionShift,
	onProductionShiftChange,
	crewId,
	onCrewChange,
	onReset,
	onExportExcel,
	canReset,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "wrap",
		}}
	>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="End"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		<Box sx={{ width: 200 }}>
			<TextField
				select
				size="small"
				label="Product Type"
				fullWidth
				value={productTypeId == null ? "" : String(productTypeId)}
				onChange={(event) =>
					onProductTypeChange(
						event.target.value === "" ? null : Number(event.target.value),
					)
				}
			>
				<MenuItem value="">All</MenuItem>
				{productTypes.map((type) => (
					<MenuItem key={type.Id} value={String(type.Id)}>
						{type.Name}
					</MenuItem>
				))}
			</TextField>
		</Box>
		{/* Keep the catalog searchable; All clears the product filter. */}
		<Box sx={{ width: 230 }}>
			<Autocomplete
				size="small"
				disableClearable
				options={productSizes}
				value={
					productSizes.find(
						(s) => s.Id === (productSizeId ?? ALL_PRODUCT_SIZE_ID),
					) ?? ALL_PRODUCT_SIZE_OPTION
				}
				getOptionLabel={(option) => option.Name}
				isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
				onChange={(_event, option) =>
					onProductSizeChange(
						option.Id === ALL_PRODUCT_SIZE_ID ? null : option.Id,
					)
				}
				autoHighlight
				renderOption={(props, option) => {
					const { key, ...rest } = props as typeof props & { key: string };
					return (
						<li key={option.Id} {...rest}>
							{option.Name}
						</li>
					);
				}}
				renderInput={(params) => <TextField {...params} label="Product" />}
			/>
		</Box>

		<Box sx={{ width: 120 }}>
			<TextField
				select
				size="small"
				fullWidth
				label="Shift"
				value={productionShift ?? ""}
				onChange={(event) =>
					onProductionShiftChange(event.target.value || null)
				}
			>
				<MenuItem value="">All</MenuItem>
				{SHIFT_OPTIONS.map((shift) => (
					<MenuItem key={shift} value={shift}>
						{shift}
					</MenuItem>
				))}
			</TextField>
		</Box>

		<Box sx={{ width: 120 }}>
			<TextField
				select
				size="small"
				fullWidth
				label="Crew"
				value={crewId ?? ""}
				onChange={(event) => onCrewChange(event.target.value || null)}
			>
				<MenuItem value="">All</MenuItem>
				{CREW_OPTIONS.map((crew) => (
					<MenuItem key={crew} value={crew}>
						{crew}
					</MenuItem>
				))}
			</TextField>
		</Box>

		<ResetFiltersButton onClick={onReset} disabled={!canReset} />

		<Box sx={{ flexGrow: 1, minWidth: 8 }} />

		<Box sx={{ width: 200 }}>
			<ExcelButton handleExportToExcel={onExportExcel} />
		</Box>
	</Box>
);
